import {
  Stepper,
  StepperProvider,
  useStepper,
} from '@/components/Molecules/Stepper'
import { buildPath } from '@/routes'
import { PropsWithChildren, useMemo } from 'react'
import { RegistrationNew } from '../hooks/ava-provider'

export const Steps = {
  Business: {
    id: 0,
    path: (registrationId: string) =>
      buildPath(
        '/applications/automated-vehicles/:registrationId/business-info',
        { registrationId }
      ),
    title: 'Company Information',
  },
  Contact: {
    id: 1,
    path: (registrationId: string) =>
      buildPath(
        '/applications/automated-vehicles/:registrationId/contact-info',
        { registrationId }
      ),
    title: 'Contact Information',
  },
  Vehicles: {
    id: 2,
    path: (registrationId: string) =>
      buildPath('/applications/automated-vehicles/:registrationId/vehicles', {
        registrationId,
      }),
    title: 'Vehicles',
  },
  Confirm: {
    id: 3,
    path: (registrationId: string) =>
      buildPath('/applications/automated-vehicles/:registrationId/certify', {
        registrationId,
      }),
    title: 'Certify & Submit',
  },
} as const

export interface AutonomousVehicleStepsProps {
  registrationId?: string
}

export function AutonomousVehicleSteps({
  registrationId,
  children,
}: PropsWithChildren<AutonomousVehicleStepsProps>) {
  const orderedSteps = useMemo(
    () =>
      Object.values(
        resolveSteps(Steps, registrationId || RegistrationNew)
      ).sort((a, b) => a.id - b.id),
    [registrationId] // Memoize once
  )

  return (
    <StepperProvider steps={orderedSteps}>
      {registrationId && (
        <Stepper
          allComplete={buildPath(
            '/applications/automated-vehicles/:registrationId/success',
            { registrationId }
          )}
          canJumpBack
        />
      )}
      {children}
    </StepperProvider>
  )
}

type ResolvedSteps<T extends Record<string, { path: (id: string) => string }>> =
  {
    [K in keyof T]: Omit<T[K], 'path'> & { path: string }
  }

const resolveSteps = <
  T extends Record<string, { path: (id: string) => string }>,
>(
  steps: T,
  registrationId: string
): ResolvedSteps<T> => {
  const out = {} as ResolvedSteps<T>
  for (const key in steps) {
    const step = steps[key]
    out[key] = { ...step, path: step.path(registrationId) }
  }
  return out
}

export function useAutonomousVehicleStepper(registrationId?: string) {
  const resolved = resolveSteps(Steps, registrationId || '')
  const orderedSteps = Object.values(resolved).sort((a, b) => a.id - b.id)

  return {
    Steps: resolved,
    ...useStepper<typeof orderedSteps>(),
  }
}
